import type { DataTableColumnDef } from 'components/data-table';

import type { StimulationRow } from 'types/well-stimulations';
import { FloatingTooltip } from 'components/floating-tooltip';
import { LaunchLink } from 'components/launch-link';
import style from './stimulations.module.scss';

export const stimulationsColumns: DataTableColumnDef<StimulationRow>[] = [
  {
    id: 'uwi14',
    accessorKey: 'uwi14',
    header: 'UWI14',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'fracFocusReport',
    accessorKey: 'fracFocusReport',
    header: 'Frac Focus Report',
    size: 180,
    meta: { type: 'text' },
    cell: (info) => {
      const fracFocusReport =
        info.getValue() as StimulationRow['fracFocusReport'];
      if (!fracFocusReport) return '—';
      return (
        <FloatingTooltip
          content={
            <div className={style.fracFocusPopover}>
              <p className={style.fracFocusPopoverContent}>
                No Frac Focus reports available for Stimulations prior to
                January 1, 2011
              </p>
            </div>
          }
          placement="top"
        >
          <LaunchLink
            href={fracFocusReport.href}
            className={style.fracFocusReportLink}
            label="Open in Frac Focus in a new tab"
            stopClickPropagation
          >
            Open in Frac Focus
          </LaunchLink>
        </FloatingTooltip>
      );
    },
  },
  {
    id: 'jobReportedEndDate',
    accessorKey: 'jobReportedEndDate',
    header: 'Report Date',
    size: 150,
    meta: { type: 'date' },
  },
  {
    id: 'proppantLbsPerFt',
    accessorKey: 'proppantLbsPerFt',
    header: 'Proppant Intensity (lbs/ft)',
    size: 220,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'fluidGalPerFt',
    accessorKey: 'fluidGalPerFt',
    header: 'Fluid Intensity (gal/ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'fluidBblPerFt',
    accessorKey: 'fluidBblPerFt',
    header: 'Fluid Intensity (bbl/ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'waterGalPerFt',
    accessorKey: 'waterGalPerFt',
    header: 'Water Intensity (gal/ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'waterBblPerFt',
    accessorKey: 'waterBblPerFt',
    header: 'Water Intensity (bbl/ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'acidGalPerFt',
    accessorKey: 'acidGalPerFt',
    header: 'Acid Intensity (gal/ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'acidBblPerFt',
    accessorKey: 'acidBblPerFt',
    header: 'Acid Intensity (bbl/ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'stageSpacingFt',
    accessorKey: 'stageSpacingFt',
    header: 'Stage Spacing (ft)',
    size: 170,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'proppantMeshSize',
    accessorKey: 'proppantMeshSize',
    header: 'Proppant Mesh Size',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'proppantType',
    accessorKey: 'proppantType',
    header: 'Proppant Type',
    size: 160,
    meta: { type: 'text' },
  },
  {
    id: 'totalProppantLbs',
    accessorKey: 'totalProppantLbs',
    header: 'Total Proppant (lbs)',
    size: 190,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'completionFluidType',
    accessorKey: 'completionFluidType',
    header: 'Completion Fluid Type',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'fluidSystem',
    accessorKey: 'fluidSystem',
    header: 'Fluid System',
    size: 150,
    meta: { type: 'text' },
  },
  {
    id: 'gelCrosslinkGal',
    accessorKey: 'gelCrosslinkGal',
    header: 'Cross Link Gel Volume (gal)',
    size: 230,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'additiveType',
    accessorKey: 'additiveType',
    header: 'Additive Type',
    size: 160,
    meta: { type: 'text' },
  },
  {
    id: 'totalNumStages',
    accessorKey: 'totalNumStages',
    header: 'Total Number of Stages',
    size: 200,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'totalFluidGal',
    accessorKey: 'totalFluidGal',
    header: 'Total Fluid (gal)',
    size: 170,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'totalWaterGal',
    accessorKey: 'totalWaterGal',
    header: 'Total Water (gal)',
    size: 170,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'totalAcidGal',
    accessorKey: 'totalAcidGal',
    header: 'Acid Volume (gal)',
    size: 170,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'perforationBaseMdFt',
    accessorKey: 'perforationBaseMdFt',
    header: 'Base Perforation MD (ft)',
    size: 210,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'perforationBaseVendorLandingZone',
    accessorKey: 'perforationBaseVendorLandingZone',
    header: 'Base Perforation Vendor Landing Zone',
    size: 300,
    meta: { type: 'text' },
  },
  {
    id: 'perforationBaseTvdFt',
    accessorKey: 'perforationBaseTvdFt',
    header: 'Base Perforation TVD (ft)',
    size: 210,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'perforationTopMdFt',
    accessorKey: 'perforationTopMdFt',
    header: 'Top Perforation MD (ft)',
    size: 200,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'perforationTopVendorLandingZone',
    accessorKey: 'perforationTopVendorLandingZone',
    header: 'Top Perforation Vendor Landing Zone',
    size: 290,
    meta: { type: 'text' },
  },
  {
    id: 'perforationTopTvdFt',
    accessorKey: 'perforationTopTvdFt',
    header: 'Top Perforation TVD (ft)',
    size: 200,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'breakdownPressurePsig',
    accessorKey: 'breakdownPressurePsig',
    header: 'Breakdown Pressure (psig)',
    size: 230,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'fracGradientPsig',
    accessorKey: 'fracGradientPsig',
    header: 'Frac Gradient (psig)',
    size: 190,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'shutInPressurePsig',
    accessorKey: 'shutInPressurePsig',
    header: 'Shut-in Pressure (psig)',
    size: 210,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'injectionTemperature',
    accessorKey: 'injectionTemperature',
    header: 'Injection Temperature (F)',
    size: 220,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'injectionPressurePsig',
    accessorKey: 'injectionPressurePsig',
    header: 'Mean Injection Pressure (psig)',
    size: 250,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'maxInjectionPressurePsig',
    accessorKey: 'maxInjectionPressurePsig',
    header: 'Max Injection Pressure (psig)',
    size: 240,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'injectionRateBpm',
    accessorKey: 'injectionRateBpm',
    header: 'Mean Injection Rate (bpm)',
    size: 220,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'maxInjectionRateBpm',
    accessorKey: 'maxInjectionRateBpm',
    header: 'Max Injection Rate (bpm)',
    size: 220,
    meta: { type: 'integer', align: 'left' },
  },
  {
    id: 'stimulationType',
    accessorKey: 'stimulationType',
    header: 'Stimulation Type',
    size: 170,
    meta: { type: 'text' },
  },
  {
    id: 'completionCompany',
    accessorKey: 'completionCompany',
    header: 'Completion Company',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'refracReasonDescription',
    accessorKey: 'refracReasonDescription',
    header: 'Refrac Reason',
    size: 160,
    meta: { type: 'text' },
  },
  {
    id: 'jobEndDate',
    accessorKey: 'jobEndDate',
    header: 'Job End Date',
    size: 150,
    meta: { type: 'date' },
  },
  {
    id: 'jobStartDate',
    accessorKey: 'jobStartDate',
    header: 'Job Start Date',
    size: 150,
    meta: { type: 'date' },
  },
  {
    id: 'jobDate',
    accessorKey: 'jobDate',
    header: 'Job Date',
    size: 130,
    meta: { type: 'date' },
  },
  {
    id: 'vendorDateCreated',
    accessorKey: 'vendorDateCreated',
    header: 'Vendor Date Created',
    size: 190,
    meta: { type: 'date' },
  },
  {
    id: 'vendorUpdatedDate',
    accessorKey: 'vendorUpdatedDate',
    header: 'Vendor Updated Date',
    size: 190,
    meta: { type: 'date' },
  },
  {
    id: 'vendorDataSource',
    accessorKey: 'vendorDataSource',
    header: 'Vendor Data Source',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: 'Source',
    size: 120,
    meta: { type: 'text' },
  },
  {
    id: 'dataSource',
    accessorKey: 'dataSource',
    header: 'Data Source',
    size: 150,
    meta: { type: 'text' },
  },
  {
    id: 'vendorComment',
    accessorKey: 'vendorComment',
    header: 'Vendor Comment',
    size: 200,
    meta: { type: 'text' },
  },
];
